import React from 'react';
import { QuotaUsage } from '../../types/api';
import { useTranslation } from 'react-i18next';

interface QuotaUsageWidgetProps {
  quotaType: 'cpu' | 'ram' | 'storage';
  usage: QuotaUsage;
  unit?: string;
  showDetails?: boolean;
}

const QuotaUsageWidget: React.FC<QuotaUsageWidgetProps> = ({
  quotaType,
  usage,
  unit,
  showDetails = true,
}) => {
  const { t } = (useTranslation as any)('organisations');
  const getQuotaLabel = () => {
    switch (quotaType) {
      case 'cpu':
        return 'CPU';
      case 'ram':
        return 'RAM';
      case 'storage':
        return t('quotaWidget.storage') as string;
      default:
        return quotaType;
    }
  };

  const getDefaultUnit = () => {
    switch (quotaType) {
      case 'cpu':
        return 'vCPU';
      case 'ram':
        return 'GB';
      case 'storage':
        return 'GB';
      default:
        return '';
    }
  };

  const getProgressBarColor = () => {
    if (usage.percentage >= 90) return 'bg-red-500';
    if (usage.percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTextColor = () => {
    if (usage.percentage >= 90) return 'text-red-400';
    if (usage.percentage >= 75) return 'text-yellow-400';
    return 'text-green-400';
  };

  const displayUnit = unit || getDefaultUnit();

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-secondary">
          {getQuotaLabel()}
        </span>
        {showDetails && (
          <span className="text-sm text-text-tertiary">
            {usage.used.toFixed(1)} / {usage.total} {displayUnit}
          </span>
        )}
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-surface-4 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
          style={{ width: `${Math.min(usage.percentage, 100)}%` }}
        ></div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className="flex items-center justify-between text-xs">
          <span className={`font-medium ${getTextColor()}`}>
            {t('quotaWidget.used', { percentage: usage.percentage.toFixed(1) })}
          </span>
          <span className="text-text-tertiary">
            {t('quotaWidget.available', { value: usage.available.toFixed(1), unit: displayUnit })}
          </span>
        </div>
      )}
    </div>
  );
};

export default QuotaUsageWidget;
